import { z } from 'zod';
import { OrganizationMembershipSchema } from './organization.types.js';
import { UserSchema } from './user.types.js';

export const AuthProviderViewSchema = z.object({
  label: z.string(),
  requiresStoredToken: z.boolean().optional(),
  kind: z.enum(['oauth', 'password']).optional(),
  hidden: z.boolean().optional(),
});

export const DevLoginStatusSchema = z.object({
  users: z.array(z.string()),
  autoLoginEmail: z.string().optional(),
});

/** Session/authentication state returned by `GET /auth/me`, including its 401 body. */
export const AuthStatusResponseSchema = z.object({
  authenticated: z.boolean(),
  authEnabled: z.boolean(),
  user: UserSchema.nullable(),
  providers: z.record(z.string(), AuthProviderViewSchema).optional(),
  memberships: z.array(OrganizationMembershipSchema).optional(),
  activeOrganizationId: z.string().nullable().optional(),
  orgsNeedingBootstrap: z.array(z.string()).optional(),
  isSuperAdmin: z.boolean().optional(),
  isAppAdmin: z.boolean().optional(),
  setupRequired: z.boolean().optional(),
  isProduction: z.boolean().optional(),
  devLogin: DevLoginStatusSchema.optional(),
  /**
   * Mirror of the platform-wide `allowSelfServeOrgCreation` auth setting, so
   * the web app can tell a user that organization creation is turned off
   * *before* they fill in the create-org form. The settings endpoint itself
   * is platform-admin only, and this single bit leaks nothing else about the
   * auth configuration. Omitted on unauthenticated responses (there is no
   * create-org affordance to gate) and defaults to `true` when absent, so an
   * older server keeps today's open self-serve behaviour.
   */
  allowSelfServeOrgCreation: z.boolean().optional(),
  ssoRequirements: z
    .array(
      z.object({
        organizationId: z.string(),
        organizationSlug: z.string(),
        organizationName: z.string(),
        satisfied: z.boolean(),
      }),
    )
    .optional(),
});
export type AuthStatusResponse = z.infer<typeof AuthStatusResponseSchema>;

export const AuthOkResponseSchema = z.object({ ok: z.boolean() });

export const DevLoginRequestSchema = z.object({
  email: z.string().email().optional(),
  login: z.string().optional(),
  name: z.string().optional(),
  providerUserId: z.string().optional(),
});

export const DevLoginResponseSchema = z.object({
  token: z.string(),
  user: UserSchema,
});

export const AuthIdentitySchema = z.object({
  id: z.string(),
  provider: z.string(),
  providerUserId: z.string(),
  providerEmail: z.string().nullable(),
  emailVerified: z.boolean(),
  createdAt: z.number(),
  lastUsedAt: z.number(),
});

export const AuthIdentitiesResponseSchema = z.object({
  identities: z.array(AuthIdentitySchema),
});
